// Permissions page — owner/manager tool for scoping each teamleader (Feb 2026).
// Per teamlead we manage:
//   • which employees they oversee (empty = all their location's employees)
//   • toggle permissions: view documents, approve leave, approve swaps,
//     edit shifts, view reports.
// State is persisted on the user record itself via PATCH /users/:id — no new
// endpoint needed. Backend `deps._public_user` already surfaces the new fields
// and `routes/users.update_user` gates writes to owners/managers only.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { avatarBg } from "@/lib/roleColors";
import { computeDisplayNames } from "@/lib/displayNames";
import SuccessCheckmark from "@/components/SuccessCheckmark";
import {
  ShieldCheck, Users as UsersIcon, FileText, ClipboardList, Repeat,
  CalendarDays, BarChart3, Check, Save, UserCog,
} from "lucide-react";

// Default permission map applied on the frontend when `user.permissions` is
// empty. These match the "what a teamlead used to be able to do before this
// page existed" — nothing is silently taken away.
const PERM_DEFAULTS = {
  can_view_documents: true,
  can_approve_leave: true,
  can_approve_swaps: true,
  can_edit_shifts: true,
  can_view_reports: true,
};

const PERM_ROWS = [
  { key: "can_view_documents",  labelKey: "perm_view_documents",  Icon: FileText },
  { key: "can_approve_leave",   labelKey: "perm_approve_leave",   Icon: ClipboardList },
  { key: "can_approve_swaps",   labelKey: "perm_approve_swaps",   Icon: Repeat },
  { key: "can_edit_shifts",     labelKey: "perm_edit_shifts",     Icon: CalendarDays },
  { key: "can_view_reports",    labelKey: "perm_view_reports",    Icon: BarChart3 },
];

function initials(name = "?") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative shrink-0 h-6 w-11 rounded-full transition-colors ${checked ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-all ${checked ? "right-0.5" : "left-0.5"}`} />
    </button>
  );
}

function TeamleadCard({ tl, employees, displayNames, onSave, t }) {
  const initialPerms = { ...PERM_DEFAULTS, ...(tl.permissions || {}) };
  const [managed, setManaged] = useState(() => tl.managed_employee_ids || []);
  const [perms, setPerms] = useState(initialPerms);
  const [busy, setBusy] = useState(false);
  const tlDisplayName = displayNames.get(tl._id) || tl.name;

  const allSelected = managed.length === 0;
  const availableEmployees = useMemo(
    () => employees.filter((e) => e.role === "employee"),
    [employees],
  );
  const toggleEmp = (id) => {
    const cur = new Set(managed);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setManaged([...cur]);
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(tl._id, { managed_employee_ids: managed, permissions: perms });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="card p-6"
      data-testid={`perm-card-${tl._id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3.5">
        <span
          className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-[15px] shrink-0"
          style={{ background: avatarBg(tl.name, tl.role) }}
        >
          {initials(tl.name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))] leading-tight truncate">
            {tlDisplayName}
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] truncate">{tl.email}</div>
        </div>
        <span className="chip chip-primary shrink-0">
          <ShieldCheck size={11} strokeWidth={2} />
          {t(`role_${tl.role}`)}
        </span>
      </div>

      {/* Section: managed employees */}
      <div className="mt-5 pt-5 border-t hairline">
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow flex items-center gap-1.5">
            <UsersIcon size={11} strokeWidth={2} />
            {t("perm_managed_employees_label")}
          </div>
          <button
            type="button"
            onClick={() => setManaged([])}
            data-testid={`perm-select-all-${tl._id}`}
            className={`text-[11px] font-semibold px-2.5 h-7 rounded-full transition-colors ${
              allSelected
                ? "bg-[hsl(var(--primary))] text-white"
                : "bg-[hsl(var(--muted))] text-[hsl(var(--text))] hover:bg-[hsl(var(--muted-strong))]"
            }`}
          >
            {allSelected && <Check size={11} strokeWidth={2.4} className="inline mr-1 -mt-0.5" />}
            {t("perm_all_employees")}
          </button>
        </div>
        <div className="text-[11.5px] text-[hsl(var(--text-muted))] mb-3 leading-snug">
          {allSelected ? t("perm_all_employees_hint") : t("perm_x_selected").replace("{n}", managed.length)}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`perm-emp-chips-${tl._id}`}>
          {availableEmployees.length === 0 ? (
            <span className="text-[12px] text-[hsl(var(--text-muted))] italic">{t("perm_no_employees_yet")}</span>
          ) : availableEmployees.map((e) => {
            const active = !allSelected && managed.includes(e._id);
            return (
              <button
                key={e._id}
                type="button"
                onClick={() => toggleEmp(e._id)}
                data-testid={`perm-emp-toggle-${tl._id}-${e._id}`}
                className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  active
                    ? "bg-[hsl(var(--primary))] border-transparent text-white"
                    : "bg-[hsl(var(--surface))] hairline text-[hsl(var(--text))] hover:bg-[hsl(var(--muted))]"
                }`}
              >
                <span
                  className="w-5 h-5 rounded-full grid place-items-center text-white text-[9.5px] font-bold shrink-0"
                  style={{ background: avatarBg(e.name, e.role) }}
                >{initials(e.name)}</span>
                <span className="truncate max-w-[120px]">{displayNames.get(e._id) || e.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section: permissions */}
      <div className="mt-5 pt-5 border-t hairline">
        <div className="eyebrow flex items-center gap-1.5 mb-3">
          <ShieldCheck size={11} strokeWidth={2} />
          {t("perm_permissions_label")}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PERM_ROWS.map((row) => (
            <label
              key={row.key}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
              data-testid={`perm-row-${tl._id}-${row.key}`}
            >
              <span className="w-8 h-8 rounded-lg bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
                <row.Icon size={13} strokeWidth={2} />
              </span>
              <span className="flex-1 text-[12.5px] font-medium text-[hsl(var(--text))]">{t(row.labelKey)}</span>
              <Toggle
                checked={!!perms[row.key]}
                onChange={(v) => setPerms({ ...perms, [row.key]: v })}
                testid={`perm-toggle-${tl._id}-${row.key}`}
              />
            </label>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 pt-5 border-t hairline flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="btn btn-primary"
          data-testid={`perm-save-${tl._id}`}
        >
          <Save size={13} strokeWidth={2} /> {busy ? t("working") : t("save")}
        </button>
      </div>
    </motion.div>
  );
}

export default function Permissions() {
  const { t } = useI18n();
  const [teamleads, setTeamleads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false); // controls SuccessCheckmark overlay
  // Compute display names across BOTH teamleaders and employees so a shared
  // name across role boundaries (rare) also gets a suffix.
  const displayNames = useMemo(
    () => computeDisplayNames([...teamleads, ...employees]),
    [teamleads, employees],
  );

  const load = async () => {
    setLoading(true);
    try {
      const [tls, emps] = await Promise.all([
        api.get("/users?role=teamlead"),
        api.get("/users?role=employee"),
      ]);
      setTeamleads(tls.data);
      setEmployees(emps.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePermissions = async (userId, payload) => {
    await api.patch(`/users/${userId}`, payload);
    await load();
    setSaved(true);
    setTimeout(() => setSaved(false), 1400);
  };

  return (
    <div data-testid="page-permissions" className="max-w-4xl mx-auto">
      <PageHeader
        eyebrow={t("permissions_eyebrow")}
        title={t("permissions_title")}
        subtitle={t("permissions_subtitle")}
      />

      {loading ? (
        <div className="grid gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="card p-6 animate-pulse h-64" />
          ))}
        </div>
      ) : teamleads.length === 0 ? (
        <EmptyState
          icon={UserCog}
          title={t("permissions_empty_title")}
          hint={t("permissions_empty_hint")}
        />
      ) : (
        <div className="grid gap-4">
          {teamleads.map((tl) => (
            <TeamleadCard
              key={tl._id}
              tl={tl}
              employees={employees}
              displayNames={displayNames}
              onSave={savePermissions}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Floating Apple-like success checkmark on save */}
      <AnimatePresence>
        {saved && (
          <motion.div
            key="perm-saved-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] grid place-items-center bg-black/25 backdrop-blur-[2px] pointer-events-none"
            data-testid="permissions-saved-overlay"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="card px-8 py-7"
            >
              <SuccessCheckmark label={t("ok_permissions_saved")} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
